import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import type { ReactNode } from 'react'
import type { User } from '../api/auth'
import { getProfile, logout as apiLogout, isAuthenticated } from '../api/auth'
import { clearAutoSound } from '../utils/autoSound'
import { claimPendingGenerations, forgetPendingGenerations } from '../utils/forgetPendingGenerations'
import { claimSceneUser } from '../api/sceneSelection'
import { setInsufficientCreditsHandler } from '../utils/credits'
import { showToast } from '../components/Toast'

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (user: User) => void
  logout: () => void
  updateCredits: (credits: number) => void
  refreshUser: () => Promise<void>
  // Сверить баланс с сервером сейчас — тем же правилом, что опрос раз в 15 с: выросший баланс — уведомление «Баланс
  // пополнен». Окно докупки зовёт после оплаты, чтобы не ждать круга опроса (refreshUser уведомление бы съел).
  syncBalance?: () => Promise<void>
  // Локальное обновление произвольных полей пользователя без обращения
  // в API. Используется когда бэк уже обновил поле как побочный эффект
  // другого запроса (например default_chat_context_mode при PATCH сессии).
  updateUser: (partial: Partial<User>) => void
}

// Экспортируем сам контекст: утилитарным хукам (useAutoSound) нужен
// ОПЦИОНАЛЬНЫЙ доступ — они работают и вне AuthProvider, где useAuth
// намеренно бросает ошибку.
// eslint-disable-next-line react-refresh/only-export-components -- контекст живёт рядом со своим провайдером
export const AuthContext = createContext<AuthContextType | null>(null)

const POLL_INTERVAL = 15000

let onBalanceTopup: ((diff: number) => void) | null = null
// eslint-disable-next-line react-refresh/only-export-components -- обработчик пополнения задают снаружи провайдера
export function setBalanceTopupHandler(handler: (diff: number) => void) {
  onBalanceTopup = handler
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  // Без токена грузить нечего — сразу «загружено» (прежде это ставил эффект вторым рендером).
  const [loading, setLoading] = useState(() => isAuthenticated())
  const prevBalanceRef = useRef<number | null>(null)

  useEffect(() => {
    if (!isAuthenticated()) return
    getProfile()
      .then(u => {
        setUser(u)
        prevBalanceRef.current = u.credits_balance
      })
      .catch(() => {
        apiLogout()
        setUser(null)
      })
      .finally(() => setLoading(false))
  }, [])

  const userId = user?.id
  // Вошёл — записи о генерациях этого браузера теперь его; чужие (другой человек, сессия кончилась без выхода) — забыть.
  useEffect(() => {
    claimPendingGenerations(userId)
    // Сцена генератора (выбранные персонажи, локации, предметы и список меню «@») — тоже данные аккаунта.
    claimSceneUser(userId)
  }, [userId])

  const syncBalance = useCallback(async () => {
    try {
      const profile = await getProfile()
      const oldBalance = prevBalanceRef.current ?? 0
      const newBalance = profile.credits_balance
      if (newBalance > oldBalance) {
        const diff = Math.round(newBalance - oldBalance)
        if (onBalanceTopup) onBalanceTopup(diff)
      }
      prevBalanceRef.current = newBalance
      // Новый объект — только когда изменилось важное: баланс или подписка (её видит кабинет, чтобы после оплаты
      // перечитать блок тарифа). Остальное без изменений — тот же объект, без лишних перерисовок.
      setUser(prev => {
        if (!prev) return profile
        if (
          prev.credits_balance === profile.credits_balance &&
          prev.subscription_status === profile.subscription_status &&
          prev.subscription_expires_at === profile.subscription_expires_at
        ) return prev
        return profile
      })
    } catch {
      /* сеть моргнула — баланс сверим на следующем круге */
    }
  }, [])

  useEffect(() => {
    if (!userId) return
    const interval = setInterval(() => { void syncBalance() }, POLL_INTERVAL)
    return () => clearInterval(interval)
  }, [userId, syncBalance])

  const login = (u: User) => {
    setUser(u)
    prevBalanceRef.current = u.credits_balance
  }

  const logout = () => {
    apiLogout()
    setUser(null)
    prevBalanceRef.current = null
    // Зеркало настройки звука — тоже пользовательские данные: на общем
    // компьютере следующий юзер не должен унаследовать чужую настройку
    clearAutoSound()
    // И чужие идущие генерации (записи о них живут до ответа сервера)
    forgetPendingGenerations()
  }

  // Стабильная функция (владелец 09.10): раньше она пересоздавалась на каждый рендер, и у страницы чата вслед за
  // ней менялась загрузка чата — эффект смены чата перезапускался после каждого списания и сбрасывал только что
  // созданный чат на «Новый чат». Функциональный setUser к тому же не затирает свежий профиль старой копией user.
  const updateCredits = useCallback((credits: number) => {
    prevBalanceRef.current = credits
    setUser((prev) => (prev ? { ...prev, credits_balance: credits } : prev))
  }, [])

  const refreshUser = useCallback(async () => {
    try {
      const profile = await getProfile()
      prevBalanceRef.current = profile.credits_balance
      setUser(profile)
    } catch {
      /* профиль не обновился — остаётся прежний */
    }
  }, [])

  const updateUser = useCallback((partial: Partial<User>) => {
    setUser((prev) => (prev ? { ...prev, ...partial } : prev))
  }, [])

  // Отказ сервера «не хватает токенов» из любой ручки (api/client → utils/credits): один тост с «Пополнить» и
  // перечитанный баланс — кнопки с предпроверкой сразу выключаются.
  useEffect(() => {
    setInsufficientCreditsHandler((text, silent) => {
      if (!silent) showToast(text, 'error', { action: { label: 'Пополнить', to: '/tariffs' } })
      void refreshUser()
    })
    return () => setInsufficientCreditsHandler(null)
  }, [refreshUser])

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, updateCredits, refreshUser, updateUser, syncBalance }}>
      {children}
    </AuthContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- хук контекста рядом с провайдером
export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}